import { useCallback, useMemo, useEffect } from 'react';

interface UseQuickViewNavigationOptions<T> {
  items: T[];
  getItemId: (item: T) => string;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}

interface UseQuickViewNavigationReturn<T> {
  selectedIndex: number | null;
  hasPrevious: boolean;
  hasNext: boolean;
  goToPrevious: () => void;
  goToNext: () => void;
  currentItem: T | null;
}

/**
 * Hook to manage prev/next navigation within a filtered list for quick views.
 * Supports keyboard navigation with arrow keys when sheet is open.
 */
export function useQuickViewNavigation<T>({
  items,
  getItemId,
  selectedId,
  onSelect,
}: UseQuickViewNavigationOptions<T>): UseQuickViewNavigationReturn<T> {
  const selectedIndex = useMemo(() => {
    if (!selectedId || items.length === 0) return null;
    const index = items.findIndex((item) => getItemId(item) === selectedId);
    return index >= 0 ? index : null;
  }, [items, selectedId, getItemId]);

  const currentItem = useMemo(() => {
    if (selectedIndex === null || selectedIndex < 0 || selectedIndex >= items.length) {
      return null;
    }
    return items[selectedIndex];
  }, [items, selectedIndex]);

  const hasPrevious = selectedIndex !== null && selectedIndex > 0;
  const hasNext = selectedIndex !== null && selectedIndex < items.length - 1;

  const goToPrevious = useCallback(() => {
    if (selectedIndex !== null && selectedIndex > 0) {
      const prevItem = items[selectedIndex - 1];
      onSelect(getItemId(prevItem));
    }
  }, [selectedIndex, items, onSelect, getItemId]);

  const goToNext = useCallback(() => {
    if (selectedIndex !== null && selectedIndex < items.length - 1) {
      const nextItem = items[selectedIndex + 1];
      onSelect(getItemId(nextItem));
    }
  }, [selectedIndex, items, onSelect, getItemId]);

  // Keyboard navigation when sheet is open
  useEffect(() => {
    if (selectedId === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      if (e.key === 'ArrowLeft' && hasPrevious) {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault();
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, hasPrevious, hasNext, goToPrevious, goToNext]);

  return {
    selectedIndex,
    hasPrevious,
    hasNext,
    goToPrevious,
    goToNext,
    currentItem,
  };
}
